import camelCase from 'lodash/camelCase'
import formValueSelector from 'redux-form/lib/formValueSelector'
import get from 'lodash/get'
import head from 'lodash/head'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import {createSelector} from 'reselect'

import {benignErrors} from '../checkoutErrorCodes'
import {CheckoutStepSlugs} from './enum'
import {MIN_AUTOCOMPLETE_CHAR_LENGTH, REDUX} from '../const'

import {isFetchingWorldpayToken} from './worldpay'
import * as ab from './ab'
import * as eventsSelectors from './events'

const getCheckoutShippingFormValues = formValueSelector(REDUX.CHECKOUT_SHIPPING_FORM)

// Getters
export const addressFieldValue = (state) =>
  getCheckoutShippingFormValues(state, 'shipping.address.address1')
export const getCheckout = (state) => get(state, 'checkout', {})
export const orderSummary = (state) => get(state, 'checkout.orderSummary', {})
export const currentCheckoutErrors = (state) => get(state, 'checkout.errors', [])
export const currentCheckoutPrompts = (state) => get(state, 'checkout.prompts', [])
export const steps = (state) => get(state, 'checkout.steps')
export const currentStep = (state) => get(state, 'checkout.currentStep')

export const precheckoutValidation = (state) => get(state, 'checkout.precheckoutValidation', {})
export const currentIsValidOrder = (state) =>
  get(precheckoutValidation(state), 'isValidOrder', false)
export const currentIsOrderValidated = (state) =>
  get(precheckoutValidation(state), 'isOrderValidated', false)
export const precheckoutValidationIsPending = (state) =>
  get(precheckoutValidation(state), 'isPending', false)
export const isUsingAccountBalance = (state) => get(state, 'checkout.isUsingAccountBalance')

// Selectors
export const userAccountBalance = createSelector(
  orderSummary,
  ({accountBalance}) => accountBalance || 0,
)

/**
 * The balance that will actually be applied to an order. Math is done on the
 * server. Used on the checkout Review Step.
 */
export const userAccountBalanceToApply = createSelector(
  orderSummary,
  ({accountBalanceToApply}) => accountBalanceToApply || 0,
)

/**
 * Combines the gates necessary for actually allowing a user to utilize their
 * account balance: feature flag on, intent to use, and non-zero balance
 *
 * @returns {Boolean}
 */
export const shouldApplyAccountBalance = createSelector(
  ab.isAccountBalanceActive,
  isUsingAccountBalance,
  userAccountBalance,
  (activeFlag, userIntent, balance) => activeFlag && userIntent && balance > 0,
)

export const orderDiscount = createSelector(orderSummary, (summary) => get(summary, 'discount', {}))

export const orderDiscountTotal = createSelector(orderDiscount, ({total}) => total || 0)

export const orderPromoCode = createSelector(orderDiscount, ({code}) => (code || '').toLowerCase())

export const orderDiscountError = createSelector(orderDiscount, ({error}) => error || '')

export const shippingAddress = createSelector(getCheckout, (checkout) =>
  get(checkout, 'shippingAddress', {}),
)

export const isSaveShippingPending = createSelector(shippingAddress, (address) =>
  get(address, 'isPending', false),
)

export const formattedShippingAddress = (formSectionName) =>
  createSelector(shippingAddress, (address) => ({
    [formSectionName]: {
      address: {...address},
    },
  }))

export const orderEventLooks = createSelector(orderSummary, ({looks}) =>
  map(looks, (look) => mapKeys(look, (_, key) => camelCase(key))),
)

export const orderSubtotal = createSelector(orderSummary, ({subtotal}) => subtotal || 0)

export const orderInsurance = createSelector(orderSummary, ({insurance}) => insurance || {})

export const orderInsuranceTotal = createSelector(orderInsurance, ({total}) => total || 0)

export const orderInsuranceQuantity = createSelector(orderInsurance, ({quantity}) => quantity || 0)

export const orderShipping = createSelector(orderSummary, ({shipping}) => shipping || {})

export const orderShippingTotal = createSelector(orderShipping, ({total}) => total || 0)

export const orderShippingQuantity = createSelector(orderShipping, ({quantity}) => quantity || 0)

export const orderShippingIsRushed = createSelector(orderShipping, (shipping) =>
  get(shipping, 'isRushed', false),
)

export const orderTax = createSelector(orderSummary, ({tax}) => tax || 0)

export const orderTotal = createSelector(orderSummary, ({total}) => total || 0)

export const orderSummaryIsPending = createSelector(orderSummary, ({isPending}) => isPending)

export const shouldShowSummaryLoader = createSelector(getCheckout, (checkout) =>
  get(checkout, 'shouldShowSummaryLoader', false),
)

export const shippingVersion = createSelector(shippingAddress, ({version}) => version)

export const billingAddress = createSelector(getCheckout, (checkout) =>
  get(checkout, 'billingAddress', {}),
)

export const isSaveBillingPending = createSelector(billingAddress, (address) =>
  get(address, 'isPending', false),
)

export const billingVersion = createSelector(billingAddress, ({version}) => version)

// regardless of the number of checkout errors, just show the first one
export const errorToDisplay = createSelector(currentCheckoutErrors, (errors) => head(errors))

export const errorToDisplayType = createSelector(errorToDisplay, (error) => get(error, 'type', ''))

export const isBenignError = createSelector(errorToDisplayType, (errorType) =>
  benignErrors.includes(errorType),
)

export const promptToDisplay = createSelector(currentCheckoutPrompts, (prompts) => head(prompts))

export const shouldExpandShippingFields = createSelector(
  addressFieldValue,
  (fieldValue) => get(fieldValue, 'length', 0) > MIN_AUTOCOMPLETE_CHAR_LENGTH,
)

export const shouldShowErrorModal = createSelector(
  currentCheckoutErrors,
  isBenignError,
  (errors, benignError) => !!errors.length && !benignError,
)

export const shouldShowCheckoutContent = createSelector(
  currentIsValidOrder,
  shouldShowErrorModal,
  (isValidOrder, showErrorModal) => isValidOrder && !showErrorModal,
)

export const shouldShowRushShipping = createSelector(
  orderShippingIsRushed,
  orderShippingTotal,
  // adding check for shipping total since the customer could have a rushed order
  // but no looks for himself, and therefore paying $0 in shipping
  (isRushed, shippingTotal) => isRushed && shippingTotal > 0,
)

export const isNavigatingForward = createSelector(getCheckout, (checkout) =>
  get(checkout, 'isNavigatingForward', true),
)

/**
 * @returns {Boolean} If we are in the coregistration funnel
 */
export const isCoregFunnel = createSelector(steps, ($steps) =>
  $steps.includes(CheckoutStepSlugs.partner),
)

export const isSavingBillingAndFetchingWorldpay = createSelector(
  isSaveBillingPending,
  isFetchingWorldpayToken,
  (isSavingBilling, isFetchingToken) => isSavingBilling || isFetchingToken,
)

/**
 * Returns an object for initializing the partner info form if we have partner
 * data available.
 *
 * @returns {Object} An object specifically catered to the partner form fields
 */
export const partnerFormInitialValues = createSelector(
  eventsSelectors.currentMetadata,
  (currentEventMetadata) => ({
    partner: {
      email: currentEventMetadata?.partnerEmail,
      firstName: currentEventMetadata?.partnerFirstName,
      lastName: currentEventMetadata?.partnerLastName,
    },
  }),
)
